class EventInstance {
	constructor(event, date) {
		this._event = event;

		this._startTime = (date ? date + event._millisecondsOfDay : event.startTime);
		this._endTime = this._startTime + event.duration;

		this._visible;
		this._active = false;
		this._selected = false;

		this._views = [];
		this._viewIndex = [];
		this._activeView = null;

		this._availableSpaces = [];

		this._groupPointer = null;

		this.checkVisible();
	}

	get activity() {
		return this._event.activity;
	}

	get event() {
		return this._event;
	}

	get rawStartTime() {
		return this._startTime;
	}

	get startTime() {
		return this.event.onMove == MOVE_START || this.event.onMove == MOVE_BOTH ?
			this._startTime + this.event.moveTime :
			this._startTime;
	}

	get endTime() {
		return this.event.onMove == MOVE_END || this.event.onMove == MOVE_BOTH ?
			this._endTime + this.event.moveTime :
			this._endTime;
	}

	get duration() {
		return this.endTime - this.startTime;
	}

	get center() {
		return this.startTime + this.duration / 2;
	}

	set visible(value) {
		this._visible = value;
		this._views.forEach(view => view.visible = value);
	}

	set active(value) {
		this._active = value;
		this._views.forEach(view => {
			view.active = value;
		});
		if (value) {
			this.event._instances.mark(this);
		} else {
			this.event._instances.mark(null);
			this.event._handleRepeat = null;
		}
	}
	get active() {
		return this._active;
	}

	get activeView() {
		if (this._activeView !== null) return this._activeView;
		for (let view of this._views) {
			if (view.layerView.isActive) return view;
		}
		return this._views[0];
	}

	get pointer() {
		if (this._groupPointer === null) {
			this._groupPointer = UI.div('group-handle-point');
			let tl = this.activeView?.layerView?.timeline || globalTimeline;
			this._groupPointer.style.left = tl.timeToOffset(this.center) + 'px';
		}
		return this._groupPointer;
	}

	set selected(value) {
		this._selected = value;
		if (!value) this._groupPointer = null;
	}

	get selected() {
		return this._selected;
	}

	get isFirstInRepeat() {
		return this.startTime == this._event.startTime;
	}

	get firstAvailableSpace () {
		return this._availableSpaces.length ? this._availableSpaces[0] : null;
	}

	get lastAvailableSpace () {
		return this._availableSpaces.length ? this._availableSpaces[this._availableSpaces.length - 1] : null;
	}

	reTime(date) {
		this._startTime = (date ? date + this._event._millisecondsOfDay : this._event.startTime);
		this._endTime = this._startTime + this.event.duration;
		this.checkVisible();
		this.resize();
	}

	checkVisible() {
		let mgr = globalTimeline;
		this.visible = mgr.viewStartMilliseconds < this._endTime
			&& this._startTime < mgr.viewEndMilliseconds;
	}

	changeLayer(newLayer) {
		this.removeViews();
		this.event.layer._instances.remove(this);
		if (newLayer instanceof Layer) {
			newLayer.addEventInstance(this);
		}
	}

	prependSpace (space) {
		this._availableSpaces.unshift(space);
	}

	appendSpace (space) {
		this._availableSpaces.push(space);
	}

	addView(layerView) {
		let view = this.getView(layerView.timeline.id, layerView);
		if (layerView.isActive) {
			this._activeView = view;
		}
		layerView.addEventView(view.elem);
		view.resize();
		view.update();
	}

	removeViews() {
		this._views.forEach(view => view.elem.remove());
		this._views = [];
		this._viewIndex = [];
	}

	getView(timelineId, layerView) {
		let i = this._viewIndex.indexOf(timelineId);
		if (-1 < i) return this._views[i];

		let view = new EventView(this, layerView, this._visible);
		this._views.push(view);
		this._viewIndex.push(timelineId);
		return view;
	}

	mouseover (view) {
		this._activeView = view;
		globalTimeline.showEventDetails(this);
	}

	mousedown (action, event) {
		stopEv(event);
		if (this.event.layer.isReadOnly) return;

		if (!this.event.instances.hasMark) this.event.instances.mark(this);

		if (globalTimeline.activateInstance(this)) this._event.beginMove(action, event);
	}
	
	mapSpaces() {
		this._availableSpaces = [this.event.layer.mapThisSpace(this)];
	}

	getAdjustments (moveTime) {
		let startTime = this._startTime + moveTime;
		let endTime = this._endTime + moveTime;
		let minDuration = globalTimeline.mode == MOVE_START_FINE || globalTimeline.mode == MOVE_END_FINE ?
			60000 : 5 * 60000;
		let duration = endTime - startTime - MIN_DURATION;
		let space = this.firstAvailableSpace;

		switch (this.event.onMove) {
			case MOVE_START:
				if (this._endTime - minDuration < startTime) {
					return (this._endTime - startTime) - minDuration;
				}
				if (startTime < space.start) {
					return space.start - startTime;
				}
				return 0;
			case MOVE_END:
				if (endTime < this._startTime + minDuration) {
					return this._startTime + minDuration - endTime;
				}
				if (space.end < endTime) {
					return space.end - endTime;
				}
				return 0;
		}

		let adjustments = [];

		while (startTime < this.firstAvailableSpace.start) {
			this.prependSpace(this.event.layer.getPrevSpace(this));
		}

		while (endTime > this.lastAvailableSpace.end) {
			this.appendSpace(this.event.layer.getNextSpace(this));
		}

		for (let i = 0, ii = this._availableSpaces.length; i < ii; i++) {
			let space = this._availableSpaces[i];

			if (startTime < space.start) {
				adjustments.push(space.start - startTime);
			}
			else if (space.end < endTime) {
				adjustments.push(space.end - endTime);
			}
			else {
				adjustments.push(0);
			};
		}
		return adjustments;
	}

	adjustmentRequired(moveTime) {
		if (this._event._onMove != MOVE_BOTH) return false;

		let startTime = this._startTime + moveTime;
		let endTime = this._endTime + moveTime;

		for (let i = 0, ii = this._availableSpaces.length; i < ii; i++) {
			let space = this._availableSpaces[i];

			if (startTime < space.start) {
				continue;
			}
			else if (space.end < endTime) {
				continue;
			}
			else {
				return false;
			};
		}
		return true;
	}

	applyMove () {
		this._startTime = this.startTime;
		this._endTime = this.endTime;
	}

	resize () {
		this._views.forEach(view => view.resize());
		this.update();
	}

	update () {
		this._views.forEach(view => view.update());
		if (this._groupPointer === null) return;
		let tl = this.activeView?.layerView?.timeline || globalTimeline;
		this._groupPointer.style.left = tl.timeToOffset(this.center) + 'px';
	}
}